import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";

import StepsNavButtons from "../Buttons/StepsNavButtons";
import TitleWithDescription from "../Headers/TitleWithDescription";
import PublishModelSchema from "../../schemas/publishModel.schema";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";
import { PRODUCT_TYPES } from "../../utils/constants";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import InformationTooltip from "../Tooltips/InformationTooltip";
import { CURRENCY_NAMES, TRIAL_DAYS } from "../../utils/constants";
import CheckBox from "../CheckBox/CheckBox";
import InputGroup from "../Inputs/InputGroup";
import Select from "react-select";
import {
    getReactSelectSmallStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";

const ManageSubscriptions = ({
    nextStepButtonText,
    nextStep,
    prevStep,
    manageSubsciptionsForm,
    setManageSubsciptionsForm,
    isBundle,
    isNewVersion,
}) => {
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const { t } = useTranslation();

    const productTypeOptions = [
        {
            name: "Single",
            label: t("Single"),
            value: "Single",
            disabled: isNewVersion,
        },
        {
            name: "Bundle",
            label: t("bundle"),
            value: "Bundle",
            disabled: isNewVersion,
        },
    ];

    const isPricePlanSkippable = () => {
        return isBundle ? false : !manageSubsciptionsForm.solo.booleanValue;
    };

    const minimumPlansSet = () => {
        return (
            isPricePlanSkippable() ||
            Boolean(
                manageSubsciptionsForm.choosenSubscriptionTypes
                    ?.annualSubscription ||
                    manageSubsciptionsForm.choosenSubscriptionTypes
                        ?.monthlySubscription
            )
        );
    };

    useEffect(() => {
        validate(
            manageSubsciptionsForm,
            PublishModelSchema.ManageSubsciptionsForm,
            setIsFormValid,
            setError,
            {
                context: {
                    monthly:
                        manageSubsciptionsForm.choosenSubscriptionTypes
                            ?.monthlySubscription,
                    annual: manageSubsciptionsForm.choosenSubscriptionTypes
                        ?.annualSubscription,
                    showModelType: !isBundle,
                    isPricePlanSkippable: isPricePlanSkippable(),
                    minimumPlansSet: minimumPlansSet(),
                },
            }
        );
    }, [manageSubsciptionsForm]);

    const handleBooleanRadioChange = (e, trueMatch) => {
        setManageSubsciptionsForm((prevForm) => {
            return {
                ...prevForm,
                [e.target.name]: {
                    value: e.target.value,
                    booleanValue: e.target.value === trueMatch,
                },
            };
        });
    };

    const handleNextPage = () => {
        setIsFormSubmitted(true);
        if (isFormValid) {
            nextStep();
        }
    };

    const handleInputChange = (e) => {
        setManageSubsciptionsForm((prevForm) => ({
            ...prevForm,
            [e.target.name]: e.target.value,
        }));
    };

    const handleSubscriptionCheckBox = (e) => {
        setManageSubsciptionsForm((prevForm) => ({
            ...prevForm,
            [`${e.target.name}${CURRENCY_NAMES.dollar}`]:
                !manageSubsciptionsForm.choosenSubscriptionTypes?.[
                    e.target.name
                ]
                    ? manageSubsciptionsForm[
                          `${e.target.name}${CURRENCY_NAMES.dollar}`
                      ]
                    : "",
            [`${e.target.name}${CURRENCY_NAMES.euro}`]: !manageSubsciptionsForm
                .choosenSubscriptionTypes?.[e.target.name]
                ? manageSubsciptionsForm[
                      `${e.target.name}${CURRENCY_NAMES.euro}`
                  ]
                : "",
            choosenSubscriptionTypes: {
                ...manageSubsciptionsForm.choosenSubscriptionTypes,
                [e.target.name]:
                    !manageSubsciptionsForm.choosenSubscriptionTypes?.[
                        e.target.name
                    ],
            },
        }));
    };

    const handleCheckBox = (e) => {
        setManageSubsciptionsForm((prevState) => ({
            ...prevState,
            [e.target.name]: !prevState[e.target.name],
        }));
    };

    const handleTrialSelect = (option, action) => {
        setManageSubsciptionsForm((prevState) => ({
            ...prevState,
            [action.name]: option,
        }));
    };

    const formatNumber = (e) => {
        setManageSubsciptionsForm((prevForm) => ({
            ...prevForm,
            [e.target.name]: e.target.value
                ? parseFloat(e.target.value).toFixed(2)
                : e.target.value,
        }));
    };

    const renderFields = (inputFields) => {
        return (
            inputFields &&
            inputFields.map((field, index) => {
                if (!field.hide) {
                    const inputGroup = field.element();
                    if (field.isArray) {
                        return (
                            <div className="flex w-full justify-between	">
                                {inputGroup &&
                                    inputGroup.map(
                                        (innerFields, innerIndex) => {
                                            return (
                                                <div
                                                    className="tags w-full"
                                                    key={`${index}-${innerIndex}`}
                                                >
                                                    <h4
                                                        className={`uppercase text-xs ${
                                                            inputGroup.required
                                                                ? "required"
                                                                : ""
                                                        }`}
                                                    >
                                                        {innerFields.label}
                                                    </h4>
                                                    {innerFields.input}
                                                </div>
                                            );
                                        }
                                    )}
                            </div>
                        );
                    } else {
                        return (
                            <div className="tags input-group" key={index}>
                                <h4
                                    className={`uppercase text-xs ${
                                        inputGroup.required ? "required" : ""
                                    }`}
                                >
                                    {inputGroup.label}
                                </h4>

                                {inputGroup.input}
                            </div>
                        );
                    }
                }
            })
        );
    };

    const fields = [
        {
            hide: isBundle,
            element: () => {
                return {
                    label: (
                        <div className="flex justify-start">
                            <Trans i18nKey="productType">Product Type</Trans>
                            <div className="self-center ml-1">
                                <InformationTooltip
                                    message={
                                        <>
                                            <div className="flex flex-col">
                                                <Trans i18nKey="singleProductTypeDescription">
                                                    <strong>Single</strong> -
                                                    The product can be sold
                                                    alone.
                                                </Trans>
                                                <Trans i18nKey="bundleProductTypeDescription">
                                                    <strong>Bundle</strong> -
                                                    The product cannot be sold
                                                    alone, it needs to be in a
                                                    Bundle.
                                                </Trans>
                                            </div>
                                        </>
                                    }
                                />
                            </div>
                        </div>
                    ),
                    input: (
                        <div
                            className={`input-group ${
                                error.solo && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <VerticalRadioButtons
                                name="solo"
                                options={productTypeOptions}
                                onChangeHandler={(e) => {
                                    handleBooleanRadioChange(
                                        e,
                                        PRODUCT_TYPES.single
                                    );
                                }}
                                checkedReference={
                                    manageSubsciptionsForm.solo.value
                                }
                            />
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.solo} />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    required: true,
                    label: (
                        <Trans i18nKey="subscriptionPlans">
                            subscription plans
                        </Trans>
                    ),
                    input: (
                        <>
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={
                                        error.choosenSubscriptionTypes
                                    }
                                />
                            )}
                        </>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: (
                        <CheckBox
                            name="hasTrial"
                            onChangeHandler={handleCheckBox}
                            label={t("allowTrial")}
                            checked={manageSubsciptionsForm.hasTrial}
                            containerClassName="ml-3"
                            checkBoxClassName="top-2"
                        />
                    ),
                    input: (
                        <div className="input-group flex items-center ml-3 gap-2">
                            <Select
                                options={Object.values(TRIAL_DAYS)}
                                className="w-1/4"
                                isSearchable={false}
                                value={manageSubsciptionsForm.trialDays}
                                onChange={handleTrialSelect}
                                name="trialDays"
                                placeholder={t("selectTrialPeriod")}
                                isDisabled={!manageSubsciptionsForm.hasTrial}
                                styles={{
                                    ...getReactSelectSmallStyling(),
                                    menu: (provided) => ({
                                        ...provided,
                                        position: "absolute",
                                    }),
                                }}
                                theme={getReactSelectTheme}
                            />
                            <p>{t("days")}</p>
                        </div>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: (
                        <CheckBox
                            name="annualSubscription"
                            onChangeHandler={handleSubscriptionCheckBox}
                            label={t("annualSubscription")}
                            checked={
                                manageSubsciptionsForm.choosenSubscriptionTypes
                                    ?.annualSubscription
                            }
                            containerClassName="ml-3"
                            checkBoxClassName="top-2"
                            disabled={
                                isPricePlanSkippable() ||
                                manageSubsciptionsForm.isAnnualRequired
                            }
                            disabledLabelMessage={t("requiredSubscriptionPlan")}
                        />
                    ),
                    input: (
                        <div className="ml-3">
                            <div className="flex gap-2">
                                <InputGroup
                                    type="number"
                                    name="annualSubscriptionDollar"
                                    disabled={
                                        !manageSubsciptionsForm
                                            .choosenSubscriptionTypes
                                            ?.annualSubscription ||
                                        isPricePlanSkippable()
                                    }
                                    icon="dollar-sign"
                                    onChangeHandler={handleInputChange}
                                    placeholder={t("usdPlaceholder")}
                                    value={
                                        manageSubsciptionsForm.annualSubscriptionDollar
                                    }
                                    onBlur={formatNumber}
                                    error={error}
                                />

                                <InputGroup
                                    type="number"
                                    name="annualSubscriptionEuro"
                                    disabled={
                                        !manageSubsciptionsForm
                                            .choosenSubscriptionTypes
                                            ?.annualSubscription ||
                                        isPricePlanSkippable()
                                    }
                                    icon="euro-sign"
                                    onChangeHandler={handleInputChange}
                                    placeholder={t("eurPlaceholder")}
                                    value={
                                        manageSubsciptionsForm.annualSubscriptionEuro
                                    }
                                    onBlur={formatNumber}
                                    error={error}
                                />
                            </div>
                            {isFormSubmitted && (
                                <ValidationError
                                    customClassNames="m-0"
                                    errorMessage={
                                        error.annualSubscriptionDollar ||
                                        error.annualSubscriptionEuro
                                    }
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: (
                        <CheckBox
                            name="monthlySubscription"
                            onChangeHandler={handleSubscriptionCheckBox}
                            label={t("monthlySubscription")}
                            checked={
                                manageSubsciptionsForm.choosenSubscriptionTypes
                                    ?.monthlySubscription
                            }
                            containerClassName="ml-3"
                            checkBoxClassName="top-2"
                            disabled={
                                isPricePlanSkippable() ||
                                manageSubsciptionsForm.isMonthlyRequired
                            }
                            disabledLabelMessage={t("requiredSubscriptionPlan")}
                        />
                    ),
                    input: (
                        <div className="ml-3">
                            <div className="flex gap-2">
                                <InputGroup
                                    type="number"
                                    name="monthlySubscriptionDollar"
                                    disabled={
                                        !manageSubsciptionsForm
                                            .choosenSubscriptionTypes
                                            ?.monthlySubscription ||
                                        isPricePlanSkippable()
                                    }
                                    icon="dollar-sign"
                                    onChangeHandler={handleInputChange}
                                    placeholder={t("usdPlaceholder")}
                                    value={
                                        manageSubsciptionsForm.monthlySubscriptionDollar
                                    }
                                    onBlur={formatNumber}
                                    error={error}
                                />

                                <InputGroup
                                    type="number"
                                    name="monthlySubscriptionEuro"
                                    disabled={
                                        !manageSubsciptionsForm
                                            .choosenSubscriptionTypes
                                            ?.monthlySubscription ||
                                        isPricePlanSkippable()
                                    }
                                    icon="euro-sign"
                                    onChangeHandler={handleInputChange}
                                    placeholder={t("eurPlaceholder")}
                                    value={
                                        manageSubsciptionsForm.monthlySubscriptionEuro
                                    }
                                    onBlur={formatNumber}
                                    error={error}
                                />
                            </div>
                            {isFormSubmitted && (
                                <ValidationError
                                    customClassNames="m-0"
                                    errorMessage={
                                        error.monthlySubscriptionDollar ||
                                        error.monthlySubscriptionEuro
                                    }
                                />
                            )}
                        </div>
                    ),
                };
            },
        },
    ];

    return (
        <>
            <div className="steps step1 show">
                <TitleWithDescription
                    title={t("publishModelStep3")}
                    description={t("publishModelStep3Paragraph")}
                />
                <div className="card-table shadow flex flex-col w-full">
                    {renderFields(fields)}
                </div>
            </div>
            <StepsNavButtons
                back={{ action: prevStep }}
                next={{
                    action: handleNextPage,
                    disabled: isFormSubmitted && !isFormValid,
                    label: nextStepButtonText,
                }}
            />
        </>
    );
};

export default ManageSubscriptions;
